import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useGSTReports } from '../hooks/useTax';

const { FiX, FiDownload, FiLoader, FiFileText, FiCalendar } = FiIcons;

const GSTReportModal = ({ isOpen, onClose, reportType }) => {
  const [generatedReport, setGeneratedReport] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const { generateGSTR1, generateGSTR3B, generateGSTR9, loading } = useGSTReports();
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      month: new Date().getMonth() + 1,
      quarter: Math.ceil((new Date().getMonth() + 1) / 3),
      year: new Date().getFullYear()
    }
  });

  const getReportTitle = () => {
    switch (reportType) {
      case 'gstr1': return 'GSTR-1 Report';
      case 'gstr3b': return 'GSTR-3B Report';
      case 'gstr9': return 'GSTR-9 Annual Report';
      default: return 'GST Report';
    }
  };

  const getReportDescription = () => {
    switch (reportType) {
      case 'gstr1': return 'Outward supplies and inward supplies liable to reverse charge';
      case 'gstr3b': return 'Monthly summary return for regular taxpayers';
      case 'gstr9': return 'Annual return for regular taxpayers';
      default: return 'GST compliance report';
    }
  };

  const handleGenerateReport = async (data) => {
    setIsGenerating(true);
    let result;

    try {
      switch (reportType) {
        case 'gstr1':
          result = await generateGSTR1(data.month, data.year);
          break;
        case 'gstr3b':
          result = await generateGSTR3B(data.quarter, data.year);
          break;
        case 'gstr9':
          result = await generateGSTR9(data.year);
          break;
        default:
          throw new Error('Invalid report type');
      }

      if (result.success) {
        setGeneratedReport(result.data);
      }
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadReport = () => {
    if (!generatedReport) return;

    const dataStr = JSON.stringify(generatedReport, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `${reportType.toUpperCase()}_${generatedReport.return_period || generatedReport.financial_year}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const renderReportContent = () => {
    if (!generatedReport) return null;

    switch (reportType) {
      case 'gstr1':
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Return Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Period</p>
                  <p className="font-semibold">{generatedReport.return_period}</p>
                </div>
                <div>
                  <p className="text-gray-600">Taxable Value</p>
                  <p className="font-semibold">{formatCurrency(generatedReport.summary?.total_taxable_value)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Tax</p>
                  <p className="font-semibold">{formatCurrency(generatedReport.summary?.total_tax)}</p>
                </div>
                <div>
                  <p className="text-gray-600">B2B Supplies</p>
                  <p className="font-semibold">{generatedReport.b2b_supplies?.length || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Tax Breakdown</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <p className="text-gray-600">IGST</p>
                  <p className="font-semibold text-blue-600">{formatCurrency(generatedReport.summary?.total_igst)}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-600">CGST</p>
                  <p className="font-semibold text-green-600">{formatCurrency(generatedReport.summary?.total_cgst)}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-600">SGST</p>
                  <p className="font-semibold text-purple-600">{formatCurrency(generatedReport.summary?.total_sgst)}</p>
                </div>
              </div>
            </div>

            {generatedReport.b2b_supplies?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">B2B Supplies (Top 5)</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">GSTIN</th>
                        <th className="px-3 py-2 text-left">Client</th>
                        <th className="px-3 py-2 text-right">Value</th>
                        <th className="px-3 py-2 text-right">Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generatedReport.b2b_supplies.slice(0, 5).map((supply, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="px-3 py-2 font-mono text-xs">{supply.gstin}</td>
                          <td className="px-3 py-2">{supply.legal_name}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(supply.taxable_value)}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(supply.igst + supply.cgst + supply.sgst)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case 'gstr3b':
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Return Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Period</p>
                  <p className="font-semibold">{generatedReport.return_period}</p>
                </div>
                <div>
                  <p className="text-gray-600">GSTIN</p>
                  <p className="font-semibold font-mono">{generatedReport.gstin}</p>
                </div>
                <div>
                  <p className="text-gray-600">Filing Date</p>
                  <p className="font-semibold">{generatedReport.filing_date}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Outward Supplies</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Taxable Value:</span>
                    <span className="font-semibold">{formatCurrency(generatedReport.outward_supplies?.taxable_value)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>IGST:</span>
                    <span className="font-semibold">{formatCurrency(generatedReport.outward_supplies?.igst)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>CGST:</span>
                    <span className="font-semibold">{formatCurrency(generatedReport.outward_supplies?.cgst)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>SGST:</span>
                    <span className="font-semibold">{formatCurrency(generatedReport.outward_supplies?.sgst)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Net Liability</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>IGST:</span>
                    <span className="font-semibold">{formatCurrency(generatedReport.net_liability?.igst)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>CGST:</span>
                    <span className="font-semibold">{formatCurrency(generatedReport.net_liability?.cgst)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>SGST:</span>
                    <span className="font-semibold">{formatCurrency(generatedReport.net_liability?.sgst)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-semibold">Total:</span>
                    <span className="font-bold text-lg">{formatCurrency(generatedReport.net_liability?.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'gstr9':
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Annual Return Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Financial Year</p>
                  <p className="font-semibold">{generatedReport.financial_year}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Turnover</p>
                  <p className="font-semibold">{formatCurrency(generatedReport.annual_summary?.total_turnover)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Tax Liability</p>
                  <p className="font-semibold">{formatCurrency(generatedReport.annual_summary?.total_tax_liability)}</p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Quarterly Breakdown</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {generatedReport.quarterly_summary?.map((quarter, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border border-purple-200">
                    <h5 className="font-semibold text-sm mb-2">Q{quarter.quarter}</h5>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span>Sales:</span>
                        <span>{formatCurrency(quarter.taxable_value)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Tax:</span>
                        <span>{formatCurrency(quarter.igst + quarter.cgst + quarter.sgst)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          <motion.div
            className="relative bg-white rounded-xl w-full max-w-4xl max-h-[95vh] overflow-y-auto shadow-xl"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{getReportTitle()}</h2>
                  <p className="text-sm text-gray-600">{getReportDescription()}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {generatedReport && (
                    <button
                      onClick={downloadReport}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                      title="Download Report"
                    >
                      <SafeIcon icon={FiDownload} className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {!generatedReport ? (
                <form onSubmit={handleSubmit(handleGenerateReport)} className="space-y-6">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2" />
                      Select Period
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {reportType === 'gstr1' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Month *
                          </label>
                          <select
                            {...register('month', { required: 'Month is required' })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {Array.from({ length: 12 }, (_, i) => (
                              <option key={i + 1} value={i + 1}>
                                {new Date(2024, i).toLocaleString('default', { month: 'long' })}
                              </option>
                            ))}
                          </select>
                          {errors.month && (
                            <p className="text-red-500 text-sm mt-1">{errors.month.message}</p>
                          )}
                        </div>
                      )}

                      {reportType === 'gstr3b' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quarter *
                          </label>
                          <select
                            {...register('quarter', { required: 'Quarter is required' })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value={1}>Q1 (Apr-Jun)</option>
                            <option value={2}>Q2 (Jul-Sep)</option>
                            <option value={3}>Q3 (Oct-Dec)</option>
                            <option value={4}>Q4 (Jan-Mar)</option>
                          </select>
                          {errors.quarter && (
                            <p className="text-red-500 text-sm mt-1">{errors.quarter.message}</p>
                          )}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Year *
                        </label>
                        <select
                          {...register('year', { required: 'Year is required' })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {Array.from({ length: 5 }, (_, i) => {
                            const year = new Date().getFullYear() - i;
                            return (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            );
                          })}
                        </select>
                        {errors.year && (
                          <p className="text-red-500 text-sm mt-1">{errors.year.message}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                      disabled={isGenerating}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isGenerating}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
                    >
                      {isGenerating ? (
                        <>
                          <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiFileText} className="w-4 h-4" />
                          <span>Generate Report</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">Generated Report</h3>
                    <button
                      onClick={() => {
                        setGeneratedReport(null);
                        reset();
                      }}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Generate New Report
                    </button>
                  </div>
                  
                  {renderReportContent()}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default GSTReportModal;